import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { CallDto } from '@shared/services/apiService/apiService';
import { CopyButton } from './CopyButton';
import { formatCallType, formatInteractionResult } from '@pages/Admin/Campaign/ui/utils';
import {
  formatDuration,
  formatLocalDateTime,
  getCallSentimentLabel,
  getElapsedSeconds,
  maskCustomerIdentifier,
  shortenId,
} from '@features/CallDetail';

interface CallDetailHeaderProps {
  call: CallDto;
}

const MetadataItem: React.FC<{
  label: string;
  value?: React.ReactNode;
  copyValue?: string | null;
  copyLabel?: string;
  copiedLabel?: string;
  sx?: SxProps<Theme>;
}> = ({ label, value, copyValue, copyLabel, copiedLabel, sx }) => {
  const { t } = useTranslation();
  const displayValue = value || t('campaignPage.unavailable');

  return (
    <Stack direction="row" alignItems="center" spacing={1} minWidth={0} sx={sx}>
      <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          component={typeof displayValue === 'string' ? 'p' : 'div'}
          title={typeof displayValue === 'string' ? displayValue : undefined}>
          {displayValue}
        </Typography>
        {copyValue ? (
          <CopyButton
            value={copyValue}
            label={copyLabel ?? t('campaignPage.callDetailHeader.copyValue')}
            copiedLabel={copiedLabel ?? t('campaignPage.callDetailHeader.copied')}
          />
        ) : null}
      </Stack>
    </Stack>
  );
};

export const CallDetailHeader: React.FC<CallDetailHeaderProps> = ({ call }) => {
  const { t } = useTranslation();
  const customerIdentifier = call.customer?.number || call.customer?.id;
  const isSquadCall = Boolean(call.squadId);
  const agentProfileName = call.agentProfileName;
  const sentiment = getCallSentimentLabel(call.sentiments);
  const duration = formatDuration(getElapsedSeconds(call.startedAt, call.endedAt));
  const callType = formatCallType(call.type);
  const endedReason = call.endedReason ? formatInteractionResult(call.endedReason) : null;

  return (
    <Box>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
          <Box minWidth={0}>
            <Typography variant="body2" color="text.secondary">
              {formatLocalDateTime(call.startedAt || call.createdAt) ||
                t('campaignPage.unavailable')}
            </Typography>
          </Box>
          <Chip label={formatInteractionResult(call.status)} color="primary" variant="outlined" />
        </Stack>

        <Box
          sx={{
            display: 'grid',
            width: '100%',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 0.5,
            mt: 0,
          }}>
          <MetadataItem
            label={t('campaignPage.callDetailHeader.callId')}
            value={shortenId(call.id)}
            copyValue={call.id}
            copyLabel={t('campaignPage.callDetailHeader.copyCallId')}
            copiedLabel={t('campaignPage.callDetailHeader.copied')}
          />
          <MetadataItem label={t('campaignPage.callDetailHeader.type')} value={callType} />
          <MetadataItem
            label={t('campaignPage.callDetailHeader.customer')}
            value={customerIdentifier ? maskCustomerIdentifier(customerIdentifier) : undefined}
            copyValue={customerIdentifier}
            copyLabel={t('campaignPage.callDetailHeader.copyCustomer')}
            copiedLabel={t('campaignPage.callDetailHeader.copied')}
          />
          {isSquadCall ? (
            <>
              <MetadataItem
                label={t('campaignPage.callDetailHeader.squad')}
                value={call.squadId ? shortenId(call.squadId) : undefined}
                copyValue={call.squadId}
                copyLabel={t('campaignPage.callDetailHeader.copySquadId')}
                copiedLabel={t('campaignPage.callDetailHeader.copied')}
              />
              <MetadataItem
                label={t('campaignPage.callDetailHeader.squadName')}
                value={call.squad?.name}
              />
              <MetadataItem
                label={t('campaignPage.callDetailHeader.squadProfiles')}
                value={agentProfileName}
              />
            </>
          ) : (
            <>
              <MetadataItem
                label={t('campaignPage.callDetailHeader.agent')}
                value={call.agentId ? shortenId(call.agentId) : undefined}
                copyValue={call.agentId}
                copyLabel={t('campaignPage.callDetailHeader.copyAgentId')}
                copiedLabel={t('campaignPage.callDetailHeader.copied')}
              />
              <MetadataItem
                label={t('campaignPage.callDetailHeader.agentProfile')}
                value={agentProfileName}
              />
            </>
          )}
          <MetadataItem
            label={t('campaignPage.callDetailHeader.sentiment')}
            value={
              sentiment ? t(`campaignPage.callDetailHeader.sentiments.${sentiment}`) : undefined
            }
          />
          <MetadataItem
            label={t('campaignPage.callDetailHeader.endedReason')}
            value={endedReason || undefined}
          />
          <MetadataItem
            label={t('campaignPage.callDetailHeader.duration')}
            value={duration || undefined}
          />
        </Box>
      </Stack>
    </Box>
  );
};
